// Cross-domain sessie-overdracht ("handoff").
//
// Het platform is white-label: elk energielabel heeft zijn eigen merkdomein
// (partners.<domein>). Een sessie leeft in localStorage en is dus per ORIGIN —
// gewoon doorlinken naar een ander merkdomein levert daar een leeg localStorage
// op, en dus het loginscherm.
//
// Daarom: mint bij de backend een eenmalige, 60s geldige code voor de HUIDIGE
// sessie, geef die mee in de URL van het doeldomein, en ruil hem daar meteen in
// voor het token. De code wordt direct na aankomst uit de adresbalk gehaald
// (history.replaceState) zodat hij niet in de browsergeschiedenis blijft staan.
//
// Gebruikt door impersonatie (heen naar het merkdomein van de gebruiker, terug
// naar dat van de beheerder). De data-scope blijft altijd server-side per
// energy_label afgedwongen; deze module regelt enkel dat de sessie meereist.

import { createHandoff } from './api';
import { getExpectedDomainForUser, isDevHost } from '../brandConfig';

const HANDOFF_PARAM = 'handoff';

const currentHost = () => String(window.location.hostname || '').toLowerCase();

// Het merkdomein waar deze gebruiker hoort, of null als er niet gesprongen moet
// worden. Op localhost/*.onrender.com (dev + technische URL) nooit springen:
// daar draait alles op één origin en zou een sprong de ontwikkelflow breken.
export function brandHopTarget(user, portalType = 'partner') {
  if (!user) return null;
  const here = currentHost();
  if (isDevHost(here)) return null;
  const target = getExpectedDomainForUser(user, portalType);
  if (!target || target.toLowerCase() === here) return null;
  return target;
}

// Haal de code uit de URL en verwijder hem meteen uit de adresbalk/geschiedenis.
// Overige query-parameters (bv. ?page=orders) blijven staan.
export function takeHandoffCode() {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get(HANDOFF_PARAM);
    if (!code) return null;
    url.searchParams.delete(HANDOFF_PARAM);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    return code;
  } catch {
    return null;
  }
}

// Spring naar `host` met de sessie achter `token`. `page` landt via ?page= op de
// juiste module. `clearLocal` wist het token op de HUIDIGE origin — gebruik dat
// bij het stoppen van impersonatie, anders blijft er een bruikbare sessie van de
// geïmpersonateerde gebruiker achter op diens merkdomein.
export async function hopToBrandDomain(host, token, { page = null, clearLocal = false } = {}) {
  const { code } = await createHandoff(token);
  if (clearLocal) {
    try { localStorage.removeItem('auth_token'); } catch { /* ignore */ }
  }
  const qs = new URLSearchParams();
  if (page) qs.set('page', page);
  qs.set(HANDOFF_PARAM, code);
  window.location.replace(`https://${host}/?${qs.toString()}`);
}
